import {
  PLATFORM_ID,
  DestroyRef,
  Directive,
  ElementRef,
  HostListener,
  Input,
  Renderer2,
  inject,
} from '@angular/core';
import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { DomSanitizer } from '@angular/platform-browser';
import { Subject, timer } from 'rxjs';
import { auditTime, takeUntil } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateService } from '@ngx-translate/core';
import { GlobalEventsService } from 'src/app/core/services/global';
import {
  calculateTooltipTransitZone,
  CustomTooltipPosition,
  calculateTooltipPlacement,
  clampTooltipToViewport,
  isPointInRect,
} from './custom-tooltip-positioning.util';
import { resolveTooltipContent } from './custom-tooltip-content-resolver.util';
import {
  CUSTOM_TOOLTIP_DEFAULTS,
  CUSTOM_TOOLTIP_INTERACTION,
  CUSTOM_TOOLTIP_LAYOUT,
} from './custom-tooltip.config';

export type { CustomTooltipPosition } from './custom-tooltip-positioning.util';

type TooltipVisibilityPhase = 'hidden' | 'showing' | 'visible' | 'hiding';

@Directive({
  selector: '[wdTooltip]',
})
export class CustomTooltipDirective {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly document = inject(DOCUMENT);
  private readonly destroyRef = inject(DestroyRef);
  private readonly elementRef = inject(ElementRef);
  private readonly renderer = inject(Renderer2);
  private readonly translateService = inject(TranslateService);
  private readonly sanitizer = inject(DomSanitizer);
  private readonly globalEventsService = inject(GlobalEventsService);
  private readonly isBrowser = isPlatformBrowser(this.platformId);

  private readonly windowRef = this.document.defaultView;

  private _tooltipText = '';
  @Input('wdTooltip')
  set tooltipText(value: string) {
    this._tooltipText = value;
    this.updateTooltipContent();
    this.scheduleTooltipReposition();
  }

  private _position: CustomTooltipPosition = CUSTOM_TOOLTIP_DEFAULTS.position;
  @Input('wdTooltipPosition')
  set position(value: CustomTooltipPosition) {
    this._position = value;
    this.scheduleTooltipReposition();
  }

  private _width: string | number = 'auto';
  @Input('wdTooltipWidth')
  set width(value: string | number) {
    this._width = value;
    this.applyWidthStyles();
    this.scheduleTooltipReposition();
  }

  private _isDisabled = false;
  @Input('wdTooltipDisable')
  set isDisabled(value: boolean) {
    this._isDisabled = value;
    if (value) {
      this.cancelTimersAndHide();
    }
  }

  private _allowHtml = false;
  @Input('wdTooltipAllowHtml')
  set allowHtml(value: boolean) {
    this._allowHtml = value;
    this.updateTooltipContent();
    this.scheduleTooltipReposition();
  }

  @Input('wdTooltipShowDelay') showDelay: number = CUSTOM_TOOLTIP_DEFAULTS.showDelay;
  @Input('wdTooltipHideDelay') hideDelay: number = CUSTOM_TOOLTIP_DEFAULTS.hideDelay;

  private readonly listenerTeardowns: Record<'tooltip' | 'document' | 'window', Array<() => void>> =
    {
      tooltip: [],
      document: [],
      window: [],
    };
  private tooltipElement: HTMLElement | null = null;
  private isFadingOut = false;
  private fadeOutTimer: ReturnType<typeof setTimeout> | null = null;
  private showTimer$ = new Subject<void>();
  private hideTimer$ = new Subject<void>();
  private interactionState: {
    isHostHovered: boolean;
    isHostFocused: boolean;
    isTooltipHovered: boolean;
    lastPointerPosition: { x: number; y: number } | null;
  } = {
    isHostHovered: false,
    isHostFocused: false,
    isTooltipHovered: false,
    lastPointerPosition: null,
  };
  private visibilityPhase: TooltipVisibilityPhase = 'hidden';
  private activePosition: CustomTooltipPosition = CUSTOM_TOOLTIP_DEFAULTS.position;
  private hostRectSnapshot: DOMRect | null = null;

  constructor() {
    this.destroyRef.onDestroy(() => {
      this.showTimer$.complete();
      this.hideTimer$.complete();
      this.detachAllListeners();
      this.removeTooltipElement();
    });
  }

  @HostListener('mouseenter')
  onMouseEnter(): void {
    this.interactionState.isHostHovered = true;

    if (this.shouldShowTooltip()) {
      this.cancelHideTimer();
      this.cancelShowTimer();
      this.visibilityPhase = 'showing';
      timer(this.showDelay)
        .pipe(takeUntil(this.showTimer$), takeUntilDestroyed(this.destroyRef))
        .subscribe(() => this.show());
    }
  }

  @HostListener('mouseleave', ['$event'])
  onMouseLeave(event?: MouseEvent): void {
    this.interactionState.isHostHovered = false;
    if (event) {
      this.interactionState.lastPointerPosition = { x: event.clientX, y: event.clientY };
    }
    this.cancelShowTimer();
    this.hideIfNotInteractingWithDelay();
  }

  @HostListener('focus')
  onFocus(): void {
    this.interactionState.isHostFocused = true;

    if (this.shouldShowTooltip()) {
      this.cancelHideTimer();
      this.cancelShowTimer();
      this.visibilityPhase = 'showing';
      this.show();
    }
  }

  @HostListener('blur')
  onBlur(): void {
    this.interactionState.isHostFocused = false;
    this.interactionState.lastPointerPosition = null;
    this.cancelShowTimer();
    this.hideIfNotInteractingWithDelay();
  }

  private shouldShowTooltip(): boolean {
    return this.isBrowser && !this._isDisabled && !!this._tooltipText;
  }

  private isInteractingWithTooltip(): boolean {
    return this.interactionState.isHostHovered || this.interactionState.isTooltipHovered;
  }

  private isPointerInTransitZone(): boolean {
    if (!this.tooltipElement || !this.interactionState.lastPointerPosition) {
      return false;
    }

    const hostRect = this.elementRef.nativeElement.getBoundingClientRect();
    const tooltipRect = this.tooltipElement.getBoundingClientRect();
    const transitZone = calculateTooltipTransitZone({
      hostRect,
      tooltipRect,
      position: this.activePosition,
      transitPadding: CUSTOM_TOOLTIP_INTERACTION.transitPadding,
      lateralTolerance:
        CUSTOM_TOOLTIP_LAYOUT.gap + CUSTOM_TOOLTIP_INTERACTION.lateralToleranceOffset,
    });

    return isPointInRect(this.interactionState.lastPointerPosition, transitZone);
  }

  private hideIfNotInteractingWithDelay(): void {
    this.hideTimer$.next();
    this.visibilityPhase = 'hiding';
    this.startPointerTracking();
    timer(this.hideDelay)
      .pipe(takeUntil(this.hideTimer$), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        if (this.visibilityPhase !== 'hiding') {
          return;
        }

        if (this.isInteractingWithTooltip()) {
          this.cancelHideTimer();
          return;
        }

        if (this.isPointerInTransitZone()) {
          this.hideIfNotInteractingWithDelay();
          return;
        }

        this.hide();
        this.cancelHideTimer();
      });
  }

  private show(): void {
    if (!this.isBrowser || !this.document.body || !this.shouldShowTooltip()) {
      this.visibilityPhase = 'hidden';
      return;
    }

    if (this.tooltipElement) {
      if (this.isFadingOut) {
        this.cancelFadeOut();
      }
      this.visibilityPhase = 'visible';
      return;
    }

    this.tooltipElement = this.renderer.createElement('div');
    this.renderer.addClass(this.tooltipElement, 'custom-tooltip');
    this.renderer.addClass(this.tooltipElement, this._position);
    this.activePosition = this._position;
    this.applyWidthStyles();
    this.updateTooltipContent();

    this.renderer.setAttribute(this.tooltipElement, 'role', 'tooltip');
    this.renderer.setAttribute(this.tooltipElement, 'aria-hidden', 'false');
    this.visibilityPhase = 'visible';

    this.renderer.appendChild(this.document.body, this.tooltipElement);
    this.hostRectSnapshot = this.elementRef.nativeElement.getBoundingClientRect();
    this.attachTooltipListeners();
    this.attachWindowListeners();

    if (!this.windowRef) {
      this.positionTooltip();
      return;
    }

    this.windowRef.requestAnimationFrame(() => {
      if (this.tooltipElement) {
        this.positionTooltip();
      }
    });
  }

  private hide(): void {
    if (!this.tooltipElement) {
      this.visibilityPhase = 'hidden';
      return;
    }

    if (this.isFadingOut) {
      return;
    }

    this.detachAllListeners();
    this.renderer.addClass(this.tooltipElement, 'tooltip-hiding');
    this.renderer.setStyle(this.tooltipElement, 'pointer-events', 'none');
    this.isFadingOut = true;

    this.fadeOutTimer = setTimeout(() => {
      this.removeTooltipElement();
    }, CUSTOM_TOOLTIP_LAYOUT.fadeOutDuration);
  }

  /** Immediately removes the tooltip from the DOM without animation. */
  private removeTooltipElement(): void {
    if (this.fadeOutTimer !== null) {
      clearTimeout(this.fadeOutTimer);
      this.fadeOutTimer = null;
    }
    this.isFadingOut = false;

    if (this.tooltipElement) {
      this.detachAllListeners();
      if (this.document.body) {
        this.renderer.removeChild(this.document.body, this.tooltipElement);
      }
      this.tooltipElement = null;
      this.hostRectSnapshot = null;
      this.interactionState.isTooltipHovered = false;
      this.interactionState.lastPointerPosition = null;
    }
    this.visibilityPhase = 'hidden';
  }

  /** Cancels an in-progress fade-out, restoring the tooltip to its visible state. */
  private cancelFadeOut(): void {
    if (!this.isFadingOut || !this.tooltipElement) {
      return;
    }

    if (this.fadeOutTimer !== null) {
      clearTimeout(this.fadeOutTimer);
      this.fadeOutTimer = null;
    }
    this.isFadingOut = false;

    this.renderer.removeClass(this.tooltipElement, 'tooltip-hiding');
    this.attachTooltipListeners();
    this.attachWindowListeners();
  }

  private attachTooltipListeners(): void {
    if (!this.tooltipElement) {
      return;
    }

    this.listenerTeardowns.tooltip.push(
      this.renderer.listen(this.tooltipElement, 'mouseenter', () => {
        this.interactionState.isTooltipHovered = true;
        this.cancelHideTimer();
      }),
    );

    this.listenerTeardowns.tooltip.push(
      this.renderer.listen(this.tooltipElement, 'mouseleave', (event: MouseEvent) => {
        this.interactionState.isTooltipHovered = false;
        this.interactionState.lastPointerPosition = { x: event.clientX, y: event.clientY };
        this.hideIfNotInteractingWithDelay();
      }),
    );
  }

  private startPointerTracking(): void {
    if (this.listenerTeardowns.document.length > 0) {
      return;
    }

    const mousemoveSubscription = this.globalEventsService
      .onDocument('mousemove', { passive: true })
      .pipe(auditTime(16))
      .subscribe((event: MouseEvent) => {
        this.interactionState.lastPointerPosition = { x: event.clientX, y: event.clientY };
      });
    this.listenerTeardowns.document.push(() => mousemoveSubscription.unsubscribe());
  }

  private stopPointerTracking(): void {
    this.listenerTeardowns.document.forEach((unlisten) => unlisten());
    this.listenerTeardowns.document = [];
  }

  private hasHostMoved(): boolean {
    if (!this.hostRectSnapshot) {
      return true;
    }
    const currentRect = this.elementRef.nativeElement.getBoundingClientRect();
    const threshold = CUSTOM_TOOLTIP_INTERACTION.scrollDismissThreshold;
    return (
      Math.abs(currentRect.top - this.hostRectSnapshot.top) > threshold ||
      Math.abs(currentRect.left - this.hostRectSnapshot.left) > threshold
    );
  }

  private attachWindowListeners(): void {
    const scrollSubscription = this.globalEventsService
      .onDocument('scroll', { capture: true, passive: true })
      .subscribe(() => {
        if (this.hasHostMoved()) {
          this.cancelTimersAndHide();
        } else {
          this.scheduleTooltipReposition();
        }
      });
    this.listenerTeardowns.window.push(() => scrollSubscription.unsubscribe());

    const resizeSubscription = this.globalEventsService
      .onWindow('resize')
      .subscribe(() => this.cancelTimersAndHide());
    this.listenerTeardowns.window.push(() => resizeSubscription.unsubscribe());
  }

  private detachAllListeners(): void {
    (Object.keys(this.listenerTeardowns) as Array<keyof typeof this.listenerTeardowns>).forEach(
      (scope) => {
        this.listenerTeardowns[scope].forEach((unlisten) => unlisten());
        this.listenerTeardowns[scope] = [];
      },
    );
  }

  private cancelTimersAndHide(): void {
    this.cancelShowTimer();
    this.cancelHideTimer();
    this.removeTooltipElement();
  }

  private cancelShowTimer(): void {
    this.showTimer$.next();

    if (this.visibilityPhase === 'showing') {
      this.visibilityPhase = this.tooltipElement ? 'visible' : 'hidden';
    }
  }

  private cancelHideTimer(): void {
    this.hideTimer$.next();
    this.stopPointerTracking();

    if (this.visibilityPhase === 'hiding') {
      this.visibilityPhase = this.tooltipElement ? 'visible' : 'hidden';
    }
  }

  private updateTooltipContent(): void {
    if (!this.tooltipElement) {
      return;
    }

    const tooltipContent = resolveTooltipContent({
      tooltipText: this._tooltipText,
      translateService: this.translateService,
      sanitizer: this.sanitizer,
      allowHtml: this._allowHtml,
    });

    this.renderer.setProperty(
      this.tooltipElement,
      this._allowHtml ? 'innerHTML' : 'textContent',
      tooltipContent,
    );
  }

  private applyWidthStyles(): void {
    if (!this.tooltipElement) {
      return;
    }

    if (this._width === 'auto') {
      this.renderer.setStyle(this.tooltipElement, 'width', 'auto');
      this.renderer.setStyle(this.tooltipElement, 'white-space', 'break-spaces');
      return;
    }

    const width = typeof this._width === 'number' ? `${this._width}px` : this._width;
    this.renderer.setStyle(this.tooltipElement, 'width', width);
    this.renderer.setStyle(this.tooltipElement, 'white-space', 'normal');
  }

  private positionTooltip(): void {
    if (!this.tooltipElement || !this.windowRef) {
      return;
    }

    const config = {
      hostRect: this.elementRef.nativeElement.getBoundingClientRect(),
      tooltipRect: this.tooltipElement.getBoundingClientRect(),
      viewportWidth: this.windowRef.innerWidth,
      viewportHeight: this.windowRef.innerHeight,
    };

    const { position: finalPosition, coords } = calculateTooltipPlacement({
      ...config,
      preferredPosition: this._position,
      gap: CUSTOM_TOOLTIP_LAYOUT.gap,
      padding: CUSTOM_TOOLTIP_LAYOUT.padding,
    });

    if (finalPosition !== this.activePosition) {
      this.renderer.removeClass(this.tooltipElement, this.activePosition);
      this.renderer.addClass(this.tooltipElement, finalPosition);
    }
    this.activePosition = finalPosition;

    const clampedCoords = clampTooltipToViewport({
      coords,
      tooltipRect: config.tooltipRect,
      padding: CUSTOM_TOOLTIP_LAYOUT.padding,
      viewportWidth: config.viewportWidth,
      viewportHeight: config.viewportHeight,
    });

    this.renderer.setStyle(this.tooltipElement, 'top', `${clampedCoords.top}px`);
    this.renderer.setStyle(this.tooltipElement, 'left', `${clampedCoords.left}px`);
  }

  private scheduleTooltipReposition(): void {
    if (!this.tooltipElement) {
      return;
    }

    if (!this.windowRef) {
      this.positionTooltip();
      return;
    }

    this.windowRef.requestAnimationFrame(() => {
      if (this.tooltipElement) {
        this.positionTooltip();
      }
    });
  }
}
